import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getFromLocalStorage } from "../utils/localStorage";
import { X, Trophy, Clock, AlertTriangle, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';

export default function Challenges() {
  const [accepted, setAccepted] = useState([]);
  const [later, setLater] = useState([]);
  const [pending, setPending] = useState([]); // new/untaken/timeout
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [timeoutCardsVersion, setTimeoutCardsVersion] = useState(0); // bump when timeoutCards change
  const [accepting, setAccepting] = useState(false);
  const navigate = useNavigate();

  // Authentication state management
  const token = getFromLocalStorage('token');
  const user = getFromLocalStorage('user');
  const isLoggedIn = (token && token.trim() !== "");
  const userEmail = user?.emailId || user?.username || user;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  // Track last known counts to detect real-time changes
  const [lastCounts, setLastCounts] = useState({ acc: 0, lat: 0, pen: 0 });

  // Listen for localStorage changes (when user takes actions on overlay)
  useEffect(() => {
    const handleStorageChange = (e) => {
      console.log('[Challenges] Storage change detected:', e.key, e.newValue);
      if (e.key === 'lastBroadcastAction' || e.key === 'timeoutCards') {
        // Immediate fetch when user takes action or timeout cards change
        console.log('[Challenges] Triggering fetch due to storage change');
        // Debounce to avoid bursts
        if (storageDebounceRef.current) clearTimeout(storageDebounceRef.current);
        storageDebounceRef.current = setTimeout(() => fetchHistory(true), 600);
        if (e.key === 'timeoutCards') {
          setTimeoutCardsVersion(v => v + 1);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Simulate timeout cards if backend doesn't save them
  const getSimulatedTimeoutCards = () => {
    try {
      const timeoutCards = JSON.parse(localStorage.getItem('timeoutCards') || '[]');
      const now = Date.now();
      // Safe debug (optional)
      console.log('[Challenges] Checking timeout cards:', {
        stored: timeoutCards.length,
        now: new Date(now).toLocaleString()
      });
      // Only show timeout cards from last 24 hours
      return timeoutCards
        .filter(card => {
          const age = now - new Date(card.createdAt).getTime();
          return age < 24 * 60 * 60 * 1000; // 24 hours
        })
        .map(card => ({ ...card, simulated: true }));
    } catch (e) {
      console.error('[Challenges] Error getting timeout cards:', e);
      return [];
    }
  };

  // Save timeout card to localStorage when no action is taken
  const saveTimeoutCard = (cardData) => {
    try {
      console.log('[Challenges] saveTimeoutCard called with:', cardData);
      const timeoutCards = JSON.parse(localStorage.getItem('timeoutCards') || '[]');
      const newTimeoutCard = {
        _id: `timeout_${cardData._id}_${Date.now()}`,
        cardId: cardData,
        status: 'timeout',
        createdAt: new Date().toISOString(),
        title: cardData.title || 'Timeout Card',
        description: cardData.description || 'Card that timed out'
      };

      // Avoid duplicates by checking very recent timeouts (last 3 seconds)
      const fewSecondsAgo = Date.now() - 3 * 1000;
      const exists = timeoutCards.some(tc => {
        const tcTime = new Date(tc.createdAt).getTime();
        return tc.cardId?._id === cardData._id && tcTime > fewSecondsAgo;
      });

      if (!exists) {
        timeoutCards.push(newTimeoutCard);
        localStorage.setItem('timeoutCards', JSON.stringify(timeoutCards));
        console.log('[Challenges] Timeout card saved:', newTimeoutCard);

        // Trigger storage event for immediate update
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'timeoutCards',
          newValue: JSON.stringify(timeoutCards)
        }));
      } else {
        console.log('[Challenges] Duplicate timeout card ignored');
      }
    } catch (e) {
      console.error('Failed to save timeout card:', e);
    }
  };

  // Expose saveTimeoutCard globally for overlay to use
  useEffect(() => {
    console.log('[Challenges] Exposing saveTimeoutCard globally');
    window.saveTimeoutCard = saveTimeoutCard;
    return () => {
      console.log('[Challenges] Cleaning up saveTimeoutCard');
      delete window.saveTimeoutCard;
    };
  }, []);

  // Background refresh control
  const isMountedRef = useRef(false);
  const firstLoadDoneRef = useRef(false);
  const fetchInFlightRef = useRef(false);
  const lastFetchIdRef = useRef(0);
  const storageDebounceRef = useRef(0);

  const fetchHistory = async (background = false) => {
    if (!token || !userEmail) { setLoading(false); return; }
    try {
      // Prevent overlapping fetches
      if (fetchInFlightRef.current) return;
      fetchInFlightRef.current = true;
      const fetchId = ++lastFetchIdRef.current;
      // Only show spinner on first load; background refreshes are silent
      if (!firstLoadDoneRef.current && !background) setLoading(true);

      // Fetch BOTH purchased card history AND broadcast card history
      let allItems = [];

      // 1. Fetch purchased cards (paid streamer cards) - STREAMER VIEW
      try {
        const purchasedRes = await axios.get(`${API_BASE_URL}/auth/user/purchased-cards/history?limit=100&view=streamer`, {
          headers: { Authorization: `Bearer ${token}`, 'User-Info': userEmail }
        });
        console.log('[Challenges] Raw purchased response:', purchasedRes.data);

        // CRITICAL FIX: Backend returns 'data' array, not 'items'
        const purchasedData = purchasedRes.data?.data || purchasedRes.data?.items || [];

        if (purchasedData.length > 0) {
          const purchasedItems = purchasedData.map(item => {
            // Backend returns actionStatus field
            const actualStatus = item.actionStatus || 'pending';
            console.log(`[Challenges] Card ${item._id}: actionStatus="${item.actionStatus}", using="${actualStatus}"`);

            return {
              ...item,
              isPurchased: true,
              isBroadcast: false,
              isFanSent: item.isFanSent || false,
              status: actualStatus,
              actionStatus: actualStatus,  // Ensure both fields are set for filtering
              // Use displayedAt from backend (sentAt || createdAt) for proper sorting
              createdAt: item.displayedAt || item.sentAt || item.createdAt || item.purchaseDate
            };
          });
          allItems = [...allItems, ...purchasedItems];
          console.log('[Challenges] Processed purchased items:', purchasedItems.map(i => ({ id: i._id, status: i.status, actionStatus: i.actionStatus })));
        }
      } catch (e) {
        console.log('No purchased cards or error:', e.message);
      }

      // 2. Fetch broadcast cards (free admin challenges)
      try {
        const broadcastRes = await axios.get(`${API_BASE_URL}/auth/user/broadcast-cards/history?limit=100`, {
          headers: { Authorization: `Bearer ${token}`, 'User-Info': userEmail }
        });
        if (broadcastRes.data?.items) {
          const broadcastItems = broadcastRes.data.items.map(item => ({
            _id: item._id,
            cardId: item.cardId,
            status: item.status,
            createdAt: item.createdAt,
            isBroadcast: true,
            isPurchased: false
          }));
          allItems = [...allItems, ...broadcastItems];
        }
      } catch (e) {
        console.log('No broadcast cards or error:', e.message);
      }

      // 3. Fetch admin challenges
      try {
        const username = user?.username || (user?.emailId ? user.emailId.split('@')[0] : '');
        if (username) {
          const adminRes = await axios.get(`${API_BASE_URL}/api/admin/challenges/streamer/${username}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (adminRes.data.status === 'success') {
            const adminItems = (adminRes.data.data || []).map(item => ({
              _id: item._id,
              cardId: item.cardId,
              status: item.actionStatus || 'pending',
              actionStatus: item.actionStatus || 'pending',
              createdAt: item.sentAt || item.createdAt,
              isAdminChallenge: true,
              isPurchased: false,
              isBroadcast: false,
              buyerUsername: 'Crezar',
              tag: 'CREZAR'
            }));
            allItems = [...allItems, ...adminItems];
          }
        }
      } catch (e) {
        console.log('No admin challenges or error:', e.message);
      }

      if (allItems.length === 0) {
        const [accRes, laterRes, pendingRes, timeoutRes, expiredRes, untakenRes, noActionRes, newRes] = await Promise.allSettled([
          axios.get(`${API_BASE_URL}/auth/user/broadcast-cards/history?status=accepted&limit=50`, {
            headers: { Authorization: `Bearer ${token}`, 'User-Info': userEmail }
          }),
          axios.get(`${API_BASE_URL}/auth/user/broadcast-cards/history?status=later&limit=50`, {
            headers: { Authorization: `Bearer ${token}`, 'User-Info': userEmail }
          }),
          axios.get(`${API_BASE_URL}/auth/user/broadcast-cards/history?status=pending&limit=50`, {
            headers: { Authorization: `Bearer ${token}`, 'User-Info': userEmail }
          }),
          axios.get(`${API_BASE_URL}/auth/user/broadcast-cards/history?status=timeout&limit=50`, {
            headers: { Authorization: `Bearer ${token}`, 'User-Info': userEmail }
          }),
          axios.get(`${API_BASE_URL}/auth/user/broadcast-cards/history?status=expired&limit=50`, {
            headers: { Authorization: `Bearer ${token}`, 'User-Info': userEmail }
          }),
          axios.get(`${API_BASE_URL}/auth/user/broadcast-cards/history?status=untaken&limit=50`, {
            headers: { Authorization: `Bearer ${token}`, 'User-Info': userEmail }
          }),
          axios.get(`${API_BASE_URL}/auth/user/broadcast-cards/history?status=no_action&limit=50`, {
            headers: { Authorization: `Bearer ${token}`, 'User-Info': userEmail }
          }),
          axios.get(`${API_BASE_URL}/auth/user/broadcast-cards/history?status=new&limit=50`, {
            headers: { Authorization: `Bearer ${token}`, 'User-Info': userEmail }
          })
        ]);

        const accItems = accRes.status === 'fulfilled' && accRes.value.data?.items ? accRes.value.data.items.map(i => ({ ...i, isBroadcast: true })) : [];
        const laterItems = laterRes.status === 'fulfilled' && laterRes.value.data?.items ? laterRes.value.data.items.map(i => ({ ...i, isBroadcast: true })) : [];
        const pendingMerged = [pendingRes, timeoutRes, expiredRes, untakenRes, noActionRes, newRes]
          .flatMap(r => r.status === 'fulfilled' && r.value.data?.items ? r.value.data.items : [])
          .map(i => ({ ...i, isBroadcast: true }));

        const accItemsClean = accItems.filter(it => !!it.cardId);
        const laterItemsClean0 = laterItems.filter(it => !!it.cardId);
        const pendingMergedClean0 = pendingMerged.filter(it => !!it.cardId);

        // Ensure items do not appear in multiple buckets: prefer Accepted > Later > Pending
        const accIds = new Set(accItemsClean.map(it => it.cardId._id));
        const laterItemsClean = laterItemsClean0.filter(it => !accIds.has(it.cardId._id));
        const laterIds = new Set(laterItemsClean.map(it => it.cardId._id));
        const pendingMergedClean = pendingMergedClean0.filter(it => !accIds.has(it.cardId._id) && !laterIds.has(it.cardId._id));

        // Remove orphaned entries whose source card was deleted at backend
        const accItemsClean2 = accItemsClean.filter(it => !!it.cardId);
        const laterItemsClean2 = laterItemsClean.filter(it => !!it.cardId);
        const pendingMergedClean2 = pendingMergedClean.filter(it => !!it.cardId);

        const seenP = new Set();
        const pendingItems = pendingMergedClean2.filter(it => {
          const id = it._id || `${it.cardId?._id || ''}-${it.createdAt || ''}-${it.status || ''}`;
          if (seenP.has(id)) return false;
          seenP.add(id);
          return true;
        });

        // Before committing results, ensure this response is the latest
        if (fetchId === lastFetchIdRef.current) {
          // Merge simulated timeouts and update only if counts changed
          const simulatedTimeouts = getSimulatedTimeoutCards();
          const finalPen = [...pendingItems, ...simulatedTimeouts];
          const newCounts = { acc: accItemsClean2.length, lat: laterItemsClean2.length, pen: finalPen.length };
          if (JSON.stringify(newCounts) !== JSON.stringify(lastCounts)) {
            setAccepted(accItemsClean2);
            setLater(laterItemsClean2);
            setPending(finalPen);
            setLastCounts(newCounts);
          }
        }
      } else {
        // Partition by status - handle both purchased and broadcast cards
        const acc = allItems.filter(i => {
          const status = i.isPurchased ? i.actionStatus : i.status;
          return (status === 'accepted') && !!i.cardId;
        });
        let lat = allItems.filter(i => {
          const status = i.isPurchased ? i.actionStatus : i.status;
          return (status === 'snoozed' || status === 'later') && !!i.cardId;
        });
        let pen = allItems.filter(i => {
          const status = i.isPurchased ? i.actionStatus : i.status;
          // Exclude dismissed cards - they should not appear in any section
          return !!i.cardId && (!status || status === 'pending') && status !== 'dismissed';
        });

        console.log('📊 Partitioned items:', { accepted: acc.length, later: lat.length, pending: pen.length });

        // De-dup across categories by engagement/transaction ID (not cardId)
        // This prevents the same engagement from appearing in multiple sections
        const accIds = new Set(acc.map(i => i._id));
        lat = lat.filter(i => !accIds.has(i._id));
        const latIds = new Set(lat.map(i => i._id));
        pen = pen.filter(i => !accIds.has(i._id) && !latIds.has(i._id));

        console.log('🔍 After dedup:', { accepted: acc.length, later: lat.length, pending: pen.length });

        if (fetchId === lastFetchIdRef.current) {
          // Merge simulated timeouts and update only if counts changed
          const simulatedTimeouts = getSimulatedTimeoutCards();
          const finalPen = [...pen, ...simulatedTimeouts];
          const newCounts = { acc: acc.length, lat: lat.length, pen: finalPen.length };
          if (JSON.stringify(newCounts) !== JSON.stringify(lastCounts)) {
            setAccepted(acc);
            setLater(lat);
            setPending(finalPen);
            setLastCounts(newCounts);
          }
        }
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching history:', err);
      setError('Failed to load challenges');
    } finally {
      fetchInFlightRef.current = false;
      if (!firstLoadDoneRef.current) {
        setLoading(false);
        firstLoadDoneRef.current = true;
      }
    }
  };

  // Restrict Challenges to creators only (not admins)
  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    if (!isLoggedIn) {
      // Not logged in: send to login
      navigate('/login');
    }
    // else allow creators (token present)
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    fetchHistory(false); // first load shows spinner
    // Moderate polling as background refresh (no spinner)
    const interval = setInterval(() => fetchHistory(true), 15000);
    return () => {
      isMountedRef.current = false;
      clearInterval(interval);
    };
  }, []);

  // Build categorized lists from localStorage timeoutCards
  const { newItems, acceptedItems, snoozedItems } = useMemo(() => {
    const simulated = getSimulatedTimeoutCards();
    const newList = [];
    const acceptedList = [];
    const snoozedList = [];

    // Categorize by status
    for (const it of simulated) {
      if (it.status === 'accepted') {
        acceptedList.push(it);
      } else if (it.status === 'later' || it.status === 'snoozed') {
        snoozedList.push(it);
      } else {
        // timeout, pending, or any other status goes to New
        newList.push(it);
      }
    }

    // Merge with backend data
    const mergedNew = [...pending, ...newList];
    const mergedAccepted = [...accepted, ...acceptedList];
    const mergedSnoozed = [...later, ...snoozedList];

    // De-dup by cardId to prevent same card appearing multiple times
    const dedup = (list) => {
      const seen = new Set();
      const result = [];
      for (const it of list) {
        const cardId = it.cardId?._id || it._id;
        if (!seen.has(cardId)) {
          seen.add(cardId);
          result.push(it);
        }
      }
      return result;
    };

    // Cross-category de-duplication: remove from New if present in Accepted or Snoozed
    const accIds = new Set(dedup(mergedAccepted).map(i => i.cardId?._id || i._id));
    const snoozeIds = new Set(dedup(mergedSnoozed).map(i => i.cardId?._id || i._id));
    const finalAccepted = dedup(mergedAccepted);
    const finalSnoozed = dedup(mergedSnoozed);
    const finalNew = dedup(mergedNew).filter(i => {
      const cid = i.cardId?._id || i._id;
      return !accIds.has(cid) && !snoozeIds.has(cid);
    });

    return {
      newItems: finalNew,
      acceptedItems: finalAccepted,
      snoozedItems: finalSnoozed
    };
  }, [pending, accepted, later, timeoutCardsVersion]);

  // Delete item helper - marks cards as dismissed in backend
  const handleDeleteItem = async (item) => {
    try {
      const confirmed = window.confirm('Are you sure you want to delete this card?');
      if (!confirmed) return;

      const same = (it) => (it._id === item._id) || (it.cardId?._id && it.cardId._id === (item.cardId?._id)) && (it.createdAt === item.createdAt);

      // Mark as dismissed in backend instead of DELETE (which doesn't exist)
      if (!item.simulated && item._id && !item._id.startsWith('timeout_')) {
        try {
          if (item.isPurchased) {
            // Mark purchased card as dismissed
            await axios.post(
              `${API_BASE_URL}/auth/user/purchased-cards/${item._id}/action`,
              { action: 'dismissed' },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'User-Info': userEmail,
                },
              }
            );
            console.log('Marked purchased card as dismissed:', item._id);
          } else if (item.isAdminChallenge) {
            // Dismiss admin challenge
            await axios.post(
              `${API_BASE_URL}/api/admin/challenges/${item._id}/action`,
              { action: 'dismissed' },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            console.log('Marked admin challenge as dismissed:', item._id);
          } else if (item.isBroadcast) {
            // For broadcast cards, mark as dismissed via broadcast endpoint
            const cardId = item.cardId?._id || item._id;
            await axios.post(
              `${API_BASE_URL}/auth/user/broadcast-cards/${cardId}/dismiss`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'User-Info': userEmail,
                },
              }
            );
            console.log('Marked broadcast card as dismissed:', cardId);
          }
        } catch (e) {
          console.warn('Backend dismiss failed:', e.response?.data || e.message);
          // Continue with local removal even if backend fails
        }
      }

      // Remove from local state
      setPending((prev) => prev.filter((it) => !same(it)));
      setAccepted((prev) => prev.filter((it) => !same(it)));
      setLater((prev) => prev.filter((it) => !same(it)));

      // If this was a simulated timeout card, remove from localStorage too
      if (item.simulated || item.status === 'timeout' || item._id?.startsWith('timeout_')) {
        try {
          const list = JSON.parse(localStorage.getItem('timeoutCards') || '[]');
          const filtered = list.filter((tc) => {
            const idMatch = (tc.cardId?._id || tc._id) !== (item.cardId?._id || item._id);
            const timeMatch = tc.createdAt !== item.createdAt;
            return idMatch || timeMatch;
          });
          localStorage.setItem('timeoutCards', JSON.stringify(filtered));
          // Notify listeners
          window.dispatchEvent(new StorageEvent('storage', { key: 'timeoutCards', newValue: JSON.stringify(filtered) }));
        } catch (e) {
          console.error('Error updating localStorage:', e);
        }
      }

      if (showModal && selected && same(selected)) {
        setShowModal(false);
        setSelected(null);
      }

      toast.success('Card deleted');

      // Trigger a background refresh to sync with backend
      setTimeout(() => fetchHistory(true), 500);
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete card');
    }
  };

  // Clear all cards in a specific section
  const handleClearAll = async (section) => {
    let items = [];
    let sectionName = '';

    if (section === 'pending') {
      items = pending;
      sectionName = 'New';
    } else if (section === 'accepted') {
      items = accepted;
      sectionName = 'Accepted';
    } else if (section === 'later') {
      items = later;
      sectionName = 'Snoozed';
    }

    if (items.length === 0) {
      toast.info(`No cards in ${sectionName} section`);
      return;
    }

    const confirmed = window.confirm(`Are you sure you want to clear all ${items.length} cards from ${sectionName}?`);
    if (!confirmed) return;

    try {
      // Mark all cards as dismissed in parallel
      const dismissPromises = items
        .filter(item => !item.simulated && item._id && !item._id.startsWith('timeout_'))
        .map(async (item) => {
          try {
            if (item.isPurchased) {
              await axios.post(
                `${API_BASE_URL}/auth/user/purchased-cards/${item._id}/action`,
                { action: 'dismissed' },
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'User-Info': userEmail,
                  },
                }
              );
            } else if (item.isBroadcast) {
              const cardId = item.cardId?._id || item._id;
              await axios.post(
                `${API_BASE_URL}/auth/user/broadcast-cards/${cardId}/dismiss`,
                {},
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'User-Info': userEmail,
                  },
                }
              );
            }
          } catch (e) {
            console.warn('Failed to dismiss card:', item._id, e.message);
          }
        });

      await Promise.allSettled(dismissPromises);

      // Clear the section
      if (section === 'pending') {
        setPending([]);
      } else if (section === 'accepted') {
        setAccepted([]);
      } else if (section === 'later') {
        setLater([]);
      }

      // Clear localStorage timeout cards if clearing pending
      if (section === 'pending') {
        localStorage.setItem('timeoutCards', JSON.stringify([]));
      }

      toast.success(`Cleared all cards from ${sectionName}`);

      // Refresh from backend
      setTimeout(() => fetchHistory(true), 500);
    } catch (error) {
      console.error('Error clearing section:', error);
      toast.error('Failed to clear all cards');
    }
  };

  // Accept currently selected challenge (from New/Snoozed)
  const handleAcceptSelected = async () => {
    if (!selected) return;
    try {
      setAccepting(true);
      const itemId = selected._id;
      if (!itemId) throw new Error('Missing item id');

      let response;
      if (selected.isBroadcast) {
        // Broadcast card - use broadcast engagement endpoint
        const cardId = selected.cardId?._id || selected._id;
        response = await axios.post(
          `${API_BASE_URL}/auth/user/broadcast-cards/${cardId}/engage`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'User-Info': userEmail,
            },
          }
        );
      } else if (selected.isAdminChallenge) {
        // Admin challenge
        response = await axios.post(
          `${API_BASE_URL}/api/admin/challenges/${selected._id}/action`,
          { action: 'accepted' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Purchased card - use transaction action endpoint
        response = await axios.post(
          `${API_BASE_URL}/auth/user/purchased-cards/${itemId}/action`,
          { action: 'accepted' },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'User-Info': userEmail,
            },
          }
        );
      }

      if (response.data.success || response.data) {
        // Optimistically move item into Accepted and remove from New/Snoozed
        const selectedCardId = selected.cardId?._id || selected._id;
        const sameByCard = (it) => (it.cardId?._id || it._id) === selectedCardId || it._id === itemId;

        // Update status to 'accepted' for both purchased and broadcast cards
        const acceptedEntry = {
          ...selected,
          status: 'accepted',
          actionStatus: selected.isPurchased ? 'accepted' : undefined,
          createdAt: new Date().toISOString()
        };

        // Prevent duplicates in Accepted
        setAccepted((prev) => {
          const filtered = prev.filter((it) => !sameByCard(it));
          return [acceptedEntry, ...filtered];
        });

        // Remove from both Pending (New) and Later (Snoozed)
        setPending((prev) => prev.filter((it) => !sameByCard(it)));
        setLater((prev) => prev.filter((it) => !sameByCard(it)));

        // Remove from localStorage timeout cards if it exists
        try {
          const timeoutCards = JSON.parse(localStorage.getItem('timeoutCards') || '[]');
          const filtered = timeoutCards.filter((tc) => (tc.cardId?._id || tc._id) !== selectedCardId && tc._id !== itemId);
          localStorage.setItem('timeoutCards', JSON.stringify(filtered));
        } catch (e) {
          console.error('Error updating localStorage:', e);
        }

        setShowModal(false);
        setSelected(null);
        toast.success('Challenge accepted!');

        // Trigger a background refresh to sync with backend
        setTimeout(() => fetchHistory(true), 500);
      } else {
        toast.error('Failed to accept challenge');
      }
    } catch (error) {
      console.error('Accept error:', error);
      toast.error('Failed to accept challenge');
    } finally {
      setAccepting(false);
    }
  };

  // Note: Views are NOT counted on Challenges page by design
  // to avoid false increments from blank/refresh/scroll states.

  const ChallengeListItem = ({ item, stableKey }) => {
    const liRef = useRef(null);
    const imageUrl = item.cardId?.imageUrl || item.imageUrl;
    const isBase64 = imageUrl?.startsWith('data:image');
    const isFullUrl = imageUrl?.startsWith('http');
    const finalImageUrl = isBase64 || isFullUrl ? imageUrl : `${API_BASE_URL}${imageUrl}`;

    return (
      <li
        key={stableKey}
        ref={liRef}
        className="bg-white/5 backdrop-blur-xl rounded-xl border border-yellow-500/20 p-6 cursor-pointer hover:bg-white/10 hover:border-yellow-500/40 transition-all group"
        onClick={() => { setSelected(item); setShowModal(true); }}
      >
        <div className="flex items-center gap-4">
          <div className="relative w-16 h-16 flex-shrink-0">
            {imageUrl ? (
              <img
                src={finalImageUrl}
                alt={item.cardId?.title || item.title}
                className="w-full h-full object-cover rounded-lg"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement.innerHTML = '<div class="w-16 h-16 rounded-lg border border-yellow-500/20 bg-white/5 flex items-center justify-center text-xs text-gray-500">No Image</div>';
                }}
              />
            ) : (
              <div className="w-16 h-16 rounded-lg border border-yellow-500/20 bg-white/5 flex items-center justify-center text-xs text-gray-500">No Image</div>
            )}
            {item.isPurchased && (item.purchaseSource === 'streamer-page' || (!item.purchaseSource && !item.isFanSent)) && (
              <div className="absolute -top-1 -right-1 bg-gradient-to-r from-pink-500 to-purple-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-lg">
                FAN GIFT
              </div>
            )}
            {item.isPurchased && (item.purchaseSource === 'dashboard' || (!item.purchaseSource && item.isFanSent)) && (
              <div className="absolute -top-1 -right-1 bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-lg">
                PAID
              </div>
            )}
            {item.isAdminChallenge && (
              <div className="absolute -top-1 -right-1 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-lg">
                CREZAR
              </div>
            )}
            {item.isBroadcast && (
              <div className="absolute -top-1 -right-1 bg-gradient-to-r from-blue-400 to-purple-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-lg">
                FREE
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-white truncate">{item.cardId?.title || item.title}</h3>
            {/* Display sender name */}
            <div className="text-sm text-yellow-400 mt-1">
              {item.isBroadcast || item.isAdminChallenge ? (
                <>Sent by: <span className="font-semibold">Crezar</span></>
              ) : (item.isPurchased && (item.buyerUsername || item.buyerEmail)) ? (
                <>Sent by: <span className="font-semibold">{item.buyerUsername || item.buyerEmail}</span></>
              ) : null}
            </div>
            <div className="text-sm text-gray-400 line-clamp-2 mt-1">{item.cardId?.description || item.description}</div>
            <div className="text-xs text-gray-500 mt-2">
              {item.isBroadcast && item.cardId?.publishedAt
                ? `Broadcasted: ${new Date(item.cardId.publishedAt).toLocaleString()}`
                : new Date(item.createdAt).toLocaleString()}
            </div>
          </div>
          <button
            className="ml-2 shrink-0 p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300 hover:text-white"
            onClick={(e) => { e.stopPropagation(); handleDeleteItem(item); }}
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </li>
    );
  };

  const ChallengeList = ({ title, items }) => {
    // Determine section type for clear all handler
    const sectionType = title === 'New' ? 'pending' : title === 'Accepted' ? 'accepted' : 'later';

    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {title === 'New' ? (
              <Clock className="w-6 h-6 text-blue-400" />
            ) : title === 'Accepted' ? (
              <Trophy className="w-6 h-6 text-yellow-400" />
            ) : (
              <Clock className="w-6 h-6 text-blue-400" />
            )}
            <h2 className="text-2xl font-bold text-white">{title}</h2>
          </div>
          {items.filter(it => !!it.cardId).length > 0 && (
            <button
              onClick={() => handleClearAll(sectionType)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 hover:text-red-300 transition-all text-sm font-medium"
              title={`Clear all ${title} cards`}
            >
              <Trash2 className="w-4 h-4" />
              Clear All
            </button>
          )}
        </div>
        {items.filter(it => !!it.cardId).length === 0 ? (
          <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-yellow-500/20 p-8 text-center">
            <p className="text-gray-400">No challenges in this category.</p>
          </div>
        ) : (
          <ul className="space-y-4">
            {items.filter(it => !!it.cardId).map((item, idx) => {
              const stableKey = item._id || item.id || item.cardId?._id || `${item.cardId?._id || 'na'}-${item.createdAt || ''}-${item.status || ''}-${idx}`;
              return <ChallengeListItem key={stableKey} stableKey={stableKey} item={item} />;
            })}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
      {/* Futuristic Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-600/10 via-transparent to-yellow-600/10 animate-pulse"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Local header removed to use global Layout header */}

      {/* Main Content */}
      <main className="relative z-10 px-6 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent mb-4">
              Challenges
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Review broadcast challenges: new (no action), accepted, or snoozed for later
            </p>
          </div>

          {/* Challenges Content */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-yellow-500/20 shadow-2xl">
            {loading ? (
              <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h2 className="text-2xl font-bold mb-6 text-white flex items-center gap-3">
                    <Trophy className="w-6 h-6 text-yellow-400" />
                    Accepted
                  </h2>
                  <div className="space-y-4">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="bg-white/5 backdrop-blur-xl rounded-xl border border-yellow-500/20 p-6 animate-pulse">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 bg-yellow-500/20 rounded-lg" />
                          <div className="flex-1 space-y-2">
                            <div className="h-4 bg-yellow-500/20 rounded w-4/5" />
                            <div className="h-3 bg-yellow-500/10 rounded w-3/5" />
                            <div className="h-3 bg-yellow-500/10 rounded w-2/5" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-6 text-white flex items-center gap-3">
                    <Clock className="w-6 h-6 text-blue-400" />
                    Snoozed
                  </h2>
                  <div className="space-y-4">
                    {Array.from({ length: 2 }).map((_, i) => (
                      <div key={i} className="bg-white/5 backdrop-blur-xl rounded-xl border border-yellow-500/20 p-6 animate-pulse">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 bg-yellow-500/20 rounded-lg" />
                          <div className="flex-1 space-y-2">
                            <div className="h-4 bg-yellow-500/20 rounded w-3/5" />
                            <div className="h-3 bg-yellow-500/10 rounded w-4/5" />
                            <div className="h-3 bg-yellow-500/10 rounded w-1/2" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : error ? (
              <div className="p-12 text-center">
                <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <AlertTriangle className="w-10 h-10 text-red-400" />
                </div>
                <h3 className="text-2xl font-bold text-red-400 mb-4">Error Loading Challenges</h3>
                <p className="text-gray-400 text-lg">{error}</p>
                <button
                  onClick={fetchHistory}
                  className="mt-6 px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black rounded-lg hover:from-yellow-500 hover:to-yellow-700 transition-all font-medium"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div className="p-8 grid grid-cols-1 xl:grid-cols-3 gap-8">
                <ChallengeList title="New" items={pending} />
                <ChallengeList title="Accepted" items={accepted} />
                <ChallengeList title="Snoozed" items={later} />
              </div>
            )}
          </div>
        </div>

        {/* Challenge Detail Modal */}
        {showModal && selected && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 px-4" onClick={() => setShowModal(false)}>
            <div className="bg-[#0b0b0d] text-white w-full max-w-2xl rounded-2xl border border-yellow-500/20 shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                <h3 className="text-xl font-bold">{selected.cardId?.title || 'Challenge'}</h3>
                <button className="text-gray-300 hover:text-white" onClick={() => setShowModal(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              {(() => {
                const imageUrl = selected.cardId?.imageUrl || selected.imageUrl;
                const isBase64 = imageUrl?.startsWith('data:image');
                const isFullUrl = imageUrl?.startsWith('http');
                const finalImageUrl = isBase64 || isFullUrl ? imageUrl : `${API_BASE_URL}${imageUrl}`;
                return imageUrl ? (
                  <img
                    src={finalImageUrl}
                    alt={selected.cardId?.title || selected.title || 'challenge'}
                    className="w-full object-contain max-h-[360px] bg-black/20"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : null;
              })()}
              <div className="p-6 space-y-3">
                {/* Display buyer info in modal */}
                {selected.isPurchased && (selected.buyerUsername || selected.buyerEmail) && (
                  <div className="text-sm text-yellow-400 mb-2 pb-2 border-b border-white/10">
                    Purchased by: <span className="font-semibold">{selected.buyerUsername || selected.buyerEmail}</span>
                  </div>
                )}
                <div className="text-gray-300 whitespace-pre-wrap">{selected.cardId?.description || selected.description || 'No description provided.'}</div>
                <div className="text-xs text-gray-500">{new Date(selected.createdAt).toLocaleString()}</div>
              </div>
              <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-3">
                {selected.status !== 'accepted' && (
                  <button
                    className={`px-4 py-2 rounded-lg bg-yellow-500/90 hover:bg-yellow-500 text-black font-semibold transition ${accepting ? 'opacity-70 cursor-not-allowed' : ''}`}
                    onClick={handleAcceptSelected}
                    disabled={accepting}
                  >
                    {accepting ? 'Accepting…' : 'Accept'}
                  </button>
                )}
                <button
                  className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-red-300 hover:text-red-200"
                  onClick={() => handleDeleteItem(selected)}
                >
                  Delete
                </button>
                <button className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20" onClick={() => setShowModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
